import React, { useEffect, useRef, useState } from "react";
import { Play, RotateCcw, Trophy, Gamepad2 } from "lucide-react";

const LANES = 3;

/* Warli-style tribal runner — ported from the legacy design portfolio tribal page */
function WarliRunner() {
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState("start"); // start | playing | gameover
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem("warli_best") || 0));

  const running = useRef(false);
  const playerY = useRef(0);
  const playerX = useRef(0);
  const obstacles = useRef([]);
  const rafId = useRef(0);
  const speed = useRef(5);
  const frames = useRef(0);
  const lane = useRef(1);
  const phaseRef = useRef("start");
  phaseRef.current = phase;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = Math.min(400, window.innerHeight * 0.6);
        playerY.current = canvas.height - 80;
        const laneW = canvas.width / LANES;
        playerX.current = lane.current * laneW + laneW / 2;
      }
    };
    window.addEventListener("resize", resize);
    resize();
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(rafId.current);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (phaseRef.current !== "playing") return;
      if (e.key === "ArrowLeft") moveLeft();
      if (e.key === "ArrowRight") moveRight();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const snapToLane = () => {
    if (!canvasRef.current) return;
    const laneW = canvasRef.current.width / LANES;
    playerX.current = lane.current * laneW + laneW / 2;
  };
  const moveLeft = () => { if (lane.current > 0) { lane.current--; snapToLane(); } };
  const moveRight = () => { if (lane.current < LANES - 1) { lane.current++; snapToLane(); } };

  const start = () => {
    setPhase("playing");
    running.current = true;
    setScore(0);
    frames.current = 0;
    speed.current = 2.8;
    obstacles.current = [];
    lane.current = 1;
    snapToLane();
    loop();
  };

  const drawRunner = (ctx, x, y) => {
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath(); ctx.arc(x, y - 20, 8, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x, y - 12); ctx.lineTo(x, y + 15); ctx.stroke();
    const t = Date.now() / 100;
    const armSwing = Math.sin(t) * 10;
    ctx.beginPath(); ctx.moveTo(x, y - 5); ctx.lineTo(x - 15, y + 5 + armSwing); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x, y - 5); ctx.lineTo(x + 15, y + 5 - armSwing); ctx.stroke();
    const legSwing = Math.cos(t) * 10;
    ctx.beginPath(); ctx.moveTo(x, y + 15); ctx.lineTo(x - 10, y + 35 + legSwing); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x, y + 15); ctx.lineTo(x + 10, y + 35 - legSwing); ctx.stroke();
  };

  const gameOver = () => {
    running.current = false;
    setPhase("gameover");
    const finalScore = Math.floor(frames.current / 10);
    setBest((b) => {
      const nb = Math.max(b, finalScore);
      localStorage.setItem("warli_best", String(nb));
      return nb;
    });
    cancelAnimationFrame(rafId.current);
  };

  const loop = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(62, 39, 35, 0.1)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const laneW = canvas.width / LANES;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.1)";
    ctx.lineWidth = 2;
    ctx.setLineDash([10, 10]);
    for (let i = 1; i < LANES; i++) {
      ctx.beginPath(); ctx.moveTo(i * laneW, 0); ctx.lineTo(i * laneW, canvas.height); ctx.stroke();
    }
    ctx.setLineDash([]);
    if (!running.current) return;

    if (speed.current < 11) speed.current += 0.004;
    frames.current += 1;
    if (frames.current % 10 === 0) setScore(Math.floor(frames.current / 10));

    const noneTooClose = obstacles.current.every((o) => o.y > 170);
    if (noneTooClose && Math.random() < 0.012 + speed.current * 0.0008) {
      const x = Math.floor(Math.random() * LANES) * laneW + laneW / 2;
      obstacles.current.push({ x, y: -50, type: Math.random() > 0.5 ? "rock" : "tree" });
    }

    obstacles.current.forEach((o, i) => {
      o.y += speed.current;
      ctx.fillStyle = "#3E2723";
      ctx.strokeStyle = "#D7CCC8";
      ctx.lineWidth = 2;
      if (o.type === "rock") {
        ctx.beginPath(); ctx.arc(o.x, o.y, 20, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(o.x - 10, o.y - 5); ctx.lineTo(o.x + 10, o.y + 5);
        ctx.moveTo(o.x + 10, o.y - 5); ctx.lineTo(o.x - 10, o.y + 5);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.moveTo(o.x, o.y - 25); ctx.lineTo(o.x + 15, o.y + 15); ctx.lineTo(o.x - 15, o.y + 15);
        ctx.closePath(); ctx.fill(); ctx.stroke();
      }
      if (Math.hypot(o.x - playerX.current, o.y - playerY.current) < 35) gameOver();
      if (o.y > canvas.height + 50) obstacles.current.splice(i, 1);
    });

    drawRunner(ctx, playerX.current, playerY.current);
    if (running.current) rafId.current = requestAnimationFrame(loop);
  };

  return (
    <div className="w-full max-w-2xl mx-auto relative" data-testid="warli-runner-game">
      <div className="relative border-4 border-[#5D4037] rounded-xl overflow-hidden bg-[#4E342E]/50 shadow-inner">
        <canvas ref={canvasRef} className="w-full h-[300px] md:h-[400px] block cursor-pointer touch-none"
                data-testid="warli-runner-canvas"
                onClick={(e) => {
                  if (phaseRef.current !== "playing") return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  if (e.clientX - rect.left < rect.width / 2) moveLeft(); else moveRight();
                }} />
        {phase !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm z-10">
            {phase === "gameover" && (
              <div className="text-center mb-6" data-testid="warli-gameover">
                <p className="text-red-400 font-bold text-xl mb-2">GAME OVER</p>
                <p className="text-white text-4xl font-bold">{score}</p>
                <p className="text-stone-400 text-sm mt-1">Best: {Math.max(score, best)}</p>
              </div>
            )}
            <button onClick={start} data-testid="warli-start-btn"
                    className="bg-[#E65100] hover:bg-[#F57C00] text-white px-8 py-4 rounded-full text-xl font-bold inline-flex items-center gap-2 border-2 border-[#FFCC80] transition-colors">
              {phase === "start" ? <Play fill="currentColor" size={20} /> : <RotateCcw size={20} />}
              {phase === "start" ? "Start Run" : "Try Again"}
            </button>
            <p className="text-white/50 mt-4 text-sm">Use Arrow Keys or Tap Left/Right sides</p>
          </div>
        )}
        {phase === "playing" && (
          <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-0 pointer-events-none">
            <div className="text-white font-bold text-xl bg-[#3E2723]/80 px-3 py-1 rounded-lg border border-[#5D4037]" data-testid="warli-score">{score}</div>
            <div className="text-[#FFCC80] text-sm flex items-center gap-1 bg-[#3E2723]/80 px-3 py-1 rounded-lg border border-[#5D4037]">
              <Trophy size={14} /> {best}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function GamesSection() {
  return (
    <div data-testid="tribe-games-section">
      <div className="text-center mb-6">
        <Gamepad2 className="mx-auto mb-3 text-[#B5793F]" size={24} strokeWidth={1.6} />
        <p className="rp-display text-xl md:text-2xl font-medium text-[#262016] mb-1">The Warli Run</p>
        <p className="rp-display rp-italic text-sm md:text-base text-[#5C5042]">
          A tribal-art runner — dodge the rocks and trees. How far can you go?
        </p>
      </div>
      <WarliRunner />
    </div>
  );
}
